/**
 * API service for the Inbound Trailer Volume page
 */
import {store} from "../../store";
import {selectStationCode, setStationCode} from "../../store/slices/shiftPlanSlice";
import { showError } from "../../store/slices/errorSlice";
import {
  TrailerData,
  ApiTrailerResponse,
  ApiTrailerData,
  ListYardTrailersRequest,
} from "./model";
import {
  API_GATEWAY_PROCESS_NAME,
  LIST_YARD_TRAILERS_USER_ID,
  LIST_YARD_TRAILERS_VISIT_REASON,
  LIST_YARD_TRAILERS_LOCALE,
} from "../../utils/constants";

import { apiRequest } from "../../services/apiMiddleware";
import {
  FETCH_TRAILERS_API,
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_INBOUND_TRAILER_TAB,
} from "../../utils/metric-utils";

/**
 * Transform API response data to match existing TrailerData interface
 */
/**
 * Infer tag status from YMS notes.
 * - If notes contain both "RED" and "TAGGED" (case-insensitive) → red tag
 * - If notes contain both "YELLOW" and "TAGGED" (case-insensitive) → yellow tag
 * - Falls back to blocked field for red tag if no note-based inference
 */
const inferTagStatus = (ymsNotes?: string, blocked?: boolean): { hasRedTag: boolean; hasYellowTag: boolean } => {
  const notes = (ymsNotes || '').toUpperCase();
  const isTagged = notes.includes('TAGGED');

  if (isTagged && notes.includes('RED')) {
    return { hasRedTag: true, hasYellowTag: false };
  }
  if (isTagged && notes.includes('YELLOW')) {
    return { hasRedTag: false, hasYellowTag: true };
  }

  // Fallback: use blocked field for red tag
  return { hasRedTag: !!blocked, hasYellowTag: false };
};

export const transformApiDataToTrailerData = (
  apiTrailers: ApiTrailerData[]
): TrailerData[] => {
  return apiTrailers.map((apiTrailer, index) => {
    const tagStatus = inferTagStatus(apiTrailer.ymsNotes, apiTrailer.blocked);

    return {
      priority: index + 1, // Assign sequential priorities
      trailerScore: apiTrailer.tscore,
      trailerId: apiTrailer.trailerId,
      carrier: apiTrailer.carrierId,
      location: apiTrailer.location,
      volume: apiTrailer.volume.total,
      volumeDetails: {
        small: apiTrailer.volume.small,
        medium: apiTrailer.volume.medium,
        large: apiTrailer.volume.large,
        rugs: apiTrailer.volume.specialty?.rugs || 0,
        tvs: apiTrailer.volume.specialty?.tvs || 0,
      },
      hasRedTag: tagStatus.hasRedTag,
      hasYellowTag: tagStatus.hasYellowTag,
      volumeOverrideAllowed: apiTrailer.volume.total === 0,
      visitId: apiTrailer.visitId,
      blocked: apiTrailer.blocked,
      blockedReason: apiTrailer.blockedReason,
      remainingItems: apiTrailer.remainingItems,
      ymsNotes: apiTrailer.ymsNotes,
      remainingPercentage:
        apiTrailer.remainingItems !== undefined &&
        apiTrailer.remainingItems !== null
          ? Math.round(
              (apiTrailer.remainingItems / apiTrailer.volume.total) * 100
            )
          : undefined,
    };
  });
};

/**
 * Call the real API to fetch trailers
 */
export const fetchTrailersFromApi = async (): Promise<TrailerData[]> => {
  const startTime = Date.now();
  let stationCode = '';
  try {
    // Get stationCode from Redux store
    const state = store.getState();
    stationCode = selectStationCode(state) || '';
    if (!stationCode) {
      const searchParam = new URLSearchParams(window.location.search.split("?")[1]);
      const stationCodeParam = searchParam.get("stationCode");
      if (stationCodeParam) {
        stationCode = stationCodeParam;
        store.dispatch(setStationCode({ stationCode: stationCode }));
      }
    }

    // Check if stationCode is still not available
    if (!stationCode) {
      store.dispatch(showError({
        title: "Warehouse ID Required",
        message: "Please select a valid warehouse ID from the dropdown"
      }));
      throw new Error("Station code is required but not provided");
    }

    // Construct request body explicitly using the ListYardTrailersRequest interface
    const requestBody: ListYardTrailersRequest = {
      warehouseId: stationCode,
      userId: LIST_YARD_TRAILERS_USER_ID,
      visitReason: LIST_YARD_TRAILERS_VISIT_REASON,
      locale: LIST_YARD_TRAILERS_LOCALE,
    };

    const requestPayload = {
      resourcePath: "/listYardTrailers",
      httpMethod: "post",
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };
    const response = await apiRequest(requestPayload);
    logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_INBOUND_TRAILER_TAB, FETCH_TRAILERS_API, MetricType.SUCCESS);
    return transformApiDataToTrailerData(response.data.trailers);
  } catch (error: any) {
    console.error("Error fetching trailers from API:", error);
    logOperationalMetric(startTime, stationCode, RCC_SHIFT_PLAN_INBOUND_TRAILER_TAB, FETCH_TRAILERS_API, MetricType.FAILED, error);
        
    // Dispatch error to Redux store to show ErrorModal
    store.dispatch(showError({
      title: "Failed to Load Trailers",
      message: "Unable to fetch trailer data from the server. Please try again later."
    }));
    
    throw error;
  }
};


/**
 * Fetch trailers data
 * @returns Promise with trailer data
 */
export const fetchTrailers = async (): Promise<TrailerData[]> => {
  return await fetchTrailersFromApi();
};


/**
 * Clear overrides for trailers
 * @returns Promise indicating success
 */
export const clearOverrides = async (): Promise<boolean> => {
  // In a real implementation, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 300);
  });
};

/**
 * Verify trailer data with overrides
 * @param trailers The trailer data including overrides
 * @returns Promise indicating success
 */
export const verifyTrailers = async (trailers?: TrailerData[]): Promise<boolean> => {
  if (trailers) {
    console.log('[Inbound Trailers API] Verifying trailers with overrides:', JSON.stringify(
      trailers.map(t => ({
        trailerId: t.trailerId,
        priority: t.priority,
        originalPriority: t.originalPriority,
        volume: t.volume,
        reorderReason: t.reorderReason,
        reorderComment: t.reorderComment,
        volumeOverrideReason: t.volumeOverrideReason,
        volumeOverrideComment: t.volumeOverrideComment,
      })),
      null, 2
    ));
  }
  // In a real implementation, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 300);
  });
};

/**
 * Save priority override for a trailer
 * @param priority The trailer priority
 * @param reason The reason for the override
 * @returns Promise indicating success
 */
export const savePriorityOverride = async (
  priority: number,
  reason: string
): Promise<boolean> => {
  // In a real implementation, this would be an API call
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(true);
    }, 300);
  });
};

/**
 * Update trailer priorities after reordering
 * @param reorderedTrailers The reordered trailer data with updated priorities
 * @returns Promise with updated trailer data
 */
export const updateTrailerPriorities = async (
  reorderedTrailers: TrailerData[]
): Promise<TrailerData[]> => {
  // In a real implementation, this would be an API call to update priorities
  return new Promise((resolve) => {
    setTimeout(() => {
      // Return the reordered trailers as if they were saved to the backend
      resolve(reorderedTrailers);
    }, 300);
  });
};
